import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportClientError } from '@/api/client-errors'
import i18n from '@/utils/i18n'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

/**
 * Корневой ErrorBoundary всей админки. Ловит ошибки рендера/жизненного
 * цикла React-дерева (то, что в обычном случае выдаёт white-screen react-router
 * с подписью «Hey developer 👋»), отсылает их в `public.client_errors` на SH
 * и показывает пользователю простое сообщение с кнопкой «Перезагрузить».
 *
 * window.onerror / unhandledrejection ставятся отдельно в main.tsx — здесь
 * только React-deree ошибки (через componentDidCatch).
 */
export class RootErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    void reportClientError({
      kind: 'boundary',
      message: error.message || String(error),
      stack: error.stack ?? null,
      componentStack: info.componentStack ?? null,
    })
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div
          style={{
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            backgroundColor: '#0b0b0c',
            color: '#f4f4f5',
            fontFamily:
              "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
          }}
        >
          <div style={{ maxWidth: 560, lineHeight: 1.5 }}>
            <h1 style={{ fontSize: 20, marginBottom: 12, fontWeight: 600 }}>
              {i18n.t('error.title')}
            </h1>
            <p style={{ marginBottom: 16, color: '#a1a1aa' }}>
              {i18n.t('error.reported')}
            </p>
            <pre
              style={{
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: 12,
                padding: 12,
                backgroundColor: '#18181b',
                border: '1px solid #27272a',
                borderRadius: 8,
                color: '#e4e4e7',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                marginBottom: 16,
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {this.state.error.message}
            </pre>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 16px',
                borderRadius: 8,
                border: 'none',
                backgroundColor: '#f97316',
                color: '#0b0b0c',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              {i18n.t('error.reload')}
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
